/**
 * React Query Configuration
 *
 * Centralized configuration for @tanstack/react-query
 * with sensible defaults for caching, retries, and error handling.
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { logger } from '../utils/logger'
import { isExpectedUpstreamClientMiss } from '../utils/apiErrorHandler'
import { isRetryableError, getRateLimitRetryDelayMs } from '../utils/retry'

let internalNavigationCount = 0
if (typeof window !== 'undefined' && window.history) {
  const originalPushState = window.history.pushState
  window.history.pushState = function (...args) {
    internalNavigationCount++
    return originalPushState.apply(this, args)
  }
}

/**
 * Map HTTP status code to appropriate telemetry log level.
 * - 403 (Forbidden) -> debug (expected RBAC/authorization restrictions, low noise)
 * - 401 (Unauthorized) -> warn (session expirations or transient unauthenticated requests)
 * - 400 (Bad Request) -> warn (expected validation warnings or duplicate states)
 * - 404 (Not Found) -> warn if direct initial navigation (pasted URL/bookmark), error if client-side transition
 * - 502 wrapping upstream 4xx / missing deployment -> warn (other side rejected the call)
 * - All other codes (422, 5xx, etc.) -> error (payload errors, server failures)
 */
function getLogLevelForStatus(
  status: number | undefined,
  error?: unknown
): 'debug' | 'warn' | 'error' {
  if (typeof status !== 'number') {
    return 'error'
  }
  if (status === 403) {
    return 'debug'
  }
  if (status === 401 || status === 400) {
    return 'warn'
  }
  if (status === 404) {
    return internalNavigationCount > 0 ? 'error' : 'warn'
  }
  if (status === 502 && isExpectedUpstreamClientMiss(error)) {
    return 'warn'
  }
  return 'error'
}

/**
 * Global error handler for queries
 * Note: Toast notifications are handled in individual mutation hooks
 * to avoid circular dependencies with the toast context
 */
function handleQueryError(error: unknown, query?: { meta?: { logLevel?: string } }) {
  const metaLogLevel = query?.meta?.logLevel as 'debug' | 'warn' | 'error' | 'silent' | undefined
  if (metaLogLevel === 'silent') {
    return
  }

  // Safely extract error details
  const errorDetails: Record<string, unknown> = {}
  let status: number | undefined

  try {
    if (error && typeof error === 'object') {
      if ('status' in error) {
        status = (error as { status?: number }).status
        errorDetails.status = status
      }
      if ('message' in error) errorDetails.message = (error as { message?: string }).message
      if ('data' in error) {
        const data = (error as { data?: unknown }).data
        if (data && typeof data === 'object' && 'message' in data) {
          errorDetails.apiMessage = (data as { message?: string }).message
        }
      }
    }
  } catch {
    errorDetails.extractionError = 'Failed to extract error details'
  }

  const logLevel = metaLogLevel || getLogLevelForStatus(status, error)

  // Use correct logger signature: level(message, error, context)
  if (logLevel === 'debug') {
    logger.debug('React Query error', {
      error: error instanceof Error ? error.message : String(error),
      ...errorDetails,
    })
  } else if (logLevel === 'warn') {
    logger.warn('React Query error', error instanceof Error ? error : undefined, errorDetails)
  } else {
    logger.error('React Query error', error instanceof Error ? error : undefined, errorDetails)
  }
}

/**
 * Global error handler for mutations
 * Note: Toast notifications are handled in individual mutation hooks
 * to avoid circular dependencies with the toast context
 */
function handleMutationError(
  error: unknown,
  _variables?: unknown,
  _context?: unknown,
  mutation?: { meta?: { logLevel?: string } }
) {
  const metaLogLevel = mutation?.meta?.logLevel as 'debug' | 'warn' | 'error' | 'silent' | undefined
  if (metaLogLevel === 'silent') {
    return
  }

  // Safely extract error details
  const errorDetails: Record<string, unknown> = {}
  let status: number | undefined

  try {
    if (error && typeof error === 'object') {
      if ('status' in error) {
        status = (error as { status?: number }).status
        errorDetails.status = status
      }
      if ('message' in error) errorDetails.message = (error as { message?: string }).message
      if ('data' in error) {
        const data = (error as { data?: unknown }).data
        if (data && typeof data === 'object') {
          if ('message' in data) errorDetails.apiMessage = (data as { message?: string }).message
          if ('detail' in data) errorDetails.apiDetail = (data as { detail?: string }).detail
        }
      }
    }
  } catch {
    errorDetails.extractionError = 'Failed to extract error details'
  }

  // Specific check for Temporal cancellation error string (transient infrastructure issue)
  const apiMsg = String(errorDetails.apiMessage || errorDetails.apiDetail || '').toLowerCase()
  const isTemporalCancelError =
    apiMsg.includes('failed to cancel') && apiMsg.includes('temporal')

  const logLevel =
    metaLogLevel || (isTemporalCancelError ? 'warn' : getLogLevelForStatus(status, error))

  // Use correct logger signature: level(message, error, context)
  if (logLevel === 'debug') {
    logger.debug('React Query mutation error', {
      error: error instanceof Error ? error.message : String(error),
      ...errorDetails,
    })
  } else if (logLevel === 'warn') {
    logger.warn(
      'React Query mutation error',
      error instanceof Error ? error : undefined,
      errorDetails
    )
  } else {
    logger.error(
      'React Query mutation error',
      error instanceof Error ? error : undefined,
      errorDetails
    )
  }
}

/**
 * Create and configure QueryClient with optimal defaults
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
    }),
    defaultOptions: {
      queries: {
        // Caching
        staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 min
        gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection (formerly cacheTime)

        // Retries
        retry: (failureCount, error) => {
          // Don't retry if it's a non-retryable error
          if (!isRetryableError(error)) {
            return false
          }
          // Retry up to 3 times for retryable errors
          return failureCount < 3
        },
        retryDelay: (attemptIndex, error) => {
          const fromServer = getRateLimitRetryDelayMs(error)
          if (fromServer != null) {
            return fromServer
          }
          return Math.min(1000 * 2 ** attemptIndex, 30000)
        },

        // Refetching behavior
        refetchOnWindowFocus: false, // Don't refetch on window focus (prevents unnecessary refetches)
        refetchOnReconnect: true, // Refetch when network reconnects
        refetchOnMount: false, // Don't refetch on mount if data is fresh (prevents filter change refetches)

        // Network mode
        networkMode: 'online', // Only run queries when online
      },
      mutations: {
        // Retries for mutations (more conservative)
        retry: (failureCount, error) => {
          // Only retry idempotent mutations (GET-like behavior)
          // Don't retry by default for safety
          if (!isRetryableError(error)) {
            return false
          }
          return failureCount < 1 // Only 1 retry for mutations
        },
        retryDelay: 1000,

        // Network mode
        networkMode: 'online',
      },
    },
  })
}

/**
 * Default QueryClient instance
 * Create once and reuse across the application
 */
export const queryClient = createQueryClient()

/**
 * AI Workflows NodePanel — shared between NodePanel (useQuery) and useAgentsQuery (invalidate).
 */
export const WORKFLOW_NODE_PANEL_QUERY_KEYS = {
  projectAgents: ['workflow-panel-agents'] as const,
  externalAgents: ['workflow-panel-external-agents'] as const,
  /** Agent Store listings for workflow canvas sidebar (paginated fetch; key includes search). */
  storeListings: ['workflow-panel-store-listings'] as const,
  /** Project-scoped custom tools for the Custom Tools tab. */
  projectTools: ['workflow-panel-project-tools'] as const,
}

/** Platform Admin denylist of standard node types hidden from the workflow palette. */
export const WORKFLOW_NODE_VISIBILITY_QUERY_KEY = ['workflow-node-visibility'] as const

/** Effective platform PAT-auth policy (Platform Admin → Security Configurations). */
export const PAT_AUTH_POLICY_QUERY_KEY = ['pat-auth-policy'] as const

/**
 * Query key factories for consistent cache keys
 *
 * These factories help maintain consistent query keys across the app,
 * making it easier to invalidate and refetch specific data.
 */
export const queryKeys = {
  // Authentication
  auth: {
    all: ['auth'] as const,
    currentUser: () => [...queryKeys.auth.all, 'current-user'] as const,
    permissions: () => [...queryKeys.auth.all, 'permissions'] as const,
  },

  // User settings (TAGP)
  settings: {
    all: ['settings'] as const,
    aiWorkflowsUi: () => [...queryKeys.settings.all, 'ai-workflows'] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all] as const,
    list: (filters?: Record<string, unknown>) =>
      filters ? [...queryKeys.projects.all, 'list', filters] : [...queryKeys.projects.all, 'list'],
    listAll: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.projects.all, 'listAll', filters] as const)
        : ([...queryKeys.projects.all, 'listAll'] as const),
    infinite: () => [...queryKeys.projects.all, 'infinite'] as const,
    detail: (id: string) => [...queryKeys.projects.all, 'detail', id],
  },

  // Agents
  agents: {
    all: ['agents'] as const,
    lists: () => [...queryKeys.agents.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.agents.lists(), { filters }] as const,
    infinite: () => [...queryKeys.agents.all, 'infinite'] as const,
    byProject: (projectId: string) => [...queryKeys.agents.all, 'project', projectId] as const,
    details: () => [...queryKeys.agents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.agents.details(), id] as const,
    chatAccess: (id: string, environment: string) =>
      [...queryKeys.agents.detail(id), 'chat-access', environment] as const,
    versions: (id: string) => [...queryKeys.agents.detail(id), 'versions'] as const,
    cloneDependencies: (id: string) =>
      [...queryKeys.agents.detail(id), 'clone-dependencies'] as const,
    guardrails: (id: string) =>
      [...queryKeys.agents.detail(id), 'guardrails'] as const,
    virtualLinkedArtifacts: (virtualAgentId: string, projectId: string) =>
      [...queryKeys.agents.all, 'virtual-linked-artifacts', virtualAgentId, projectId] as const,
  },

  // Prompts
  prompts: {
    all: ['prompts'] as const,
    lists: () => [...queryKeys.prompts.all, 'list'] as const,
    /** Full unpaginated list for filter dropdown facets (categories, etc.) */
    listAllForFilters: () => [...queryKeys.prompts.all, 'listAllForFilters'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.prompts.lists(), { filters }] as const,
    details: () => [...queryKeys.prompts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.prompts.details(), id] as const,
    versions: (id: string) => [...queryKeys.prompts.detail(id), 'versions'] as const,
  },

  // Models
  models: {
    all: ['models'] as const,
    lists: () => [...queryKeys.models.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.models.lists(), { filters }] as const,
    details: () => [...queryKeys.models.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.models.details(), id] as const,
    // Reranker models (Phase 5 RAG Enhancement)
    rerankers: () => [...queryKeys.models.all, 'rerankers'] as const,
  },

  // Connectors
  connectors: {
    all: ['connectors'] as const,
    serviceTypes: () => [...queryKeys.connectors.all, 'service-types'] as const,
    lists: () => [...queryKeys.connectors.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.connectors.lists(), { filters }] as const,
    infinite: (category?: string) => [...queryKeys.connectors.all, 'infinite', { category }] as const,
    details: () => [...queryKeys.connectors.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.connectors.details(), id] as const,
    tables: (id: string) => [...queryKeys.connectors.detail(id), 'tables'] as const,
    tableData: (tableId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.connectors.all, 'table-data', tableId, { filters }] as const,
    filterFacets: (filters?: Record<string, unknown>) =>
      [...queryKeys.connectors.all, 'filter-facets', { filters }] as const,
    registeredTables: (filters?: Record<string, unknown>) =>
      [...queryKeys.connectors.all, 'registered-tables', { filters }] as const,
  },

  // Assets
  assets: {
    all: ['assets'] as const,
    lists: () => [...queryKeys.assets.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.assets.lists(), { filters }] as const,
    details: () => [...queryKeys.assets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.assets.details(), id] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // Admin panel (tenant admin)
  admin: {
    all: ['admin'] as const,
    users: {
      all: () => [...queryKeys.admin.all, 'users'] as const,
      lists: () => [...queryKeys.admin.users.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) => [...queryKeys.admin.users.lists(), filters] as const,
    },
    roles: {
      all: () => [...queryKeys.admin.all, 'roles'] as const,
      lists: () => [...queryKeys.admin.roles.all(), 'list'] as const,
      list: (filters: Record<string, unknown>) => [...queryKeys.admin.roles.lists(), filters] as const,
      unpaginated: (tenantId?: string) =>
        [...queryKeys.admin.roles.all(), 'unpaginated', tenantId ?? 'default'] as const,
    },
    usersPicker: (search: string) => [...queryKeys.admin.all, 'users-picker', search] as const,
  },

  // External Agents
  externalAgents: {
    all: ['external-agents'] as const,
    lists: () => [...queryKeys.externalAgents.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.externalAgents.lists(), { filters }] as const,
    details: () => [...queryKeys.externalAgents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.externalAgents.details(), id] as const,
  },

  // IAM (Identity and Access Management)
  iam: {
    all: ['iam'] as const,
    lists: () => [...queryKeys.iam.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.iam.lists(), { filters }] as const,
    details: () => [...queryKeys.iam.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.iam.details(), id] as const,
  },

  // Skills
  skills: {
    all: ['skills'] as const,
    lists: () => [...queryKeys.skills.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.skills.lists(), { filters }] as const,
    details: () => [...queryKeys.skills.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.skills.details(), id] as const,
    versions: (id: string) => [...queryKeys.skills.detail(id), 'versions'] as const,
    content: (id: string, versionId?: string) =>
      [...queryKeys.skills.detail(id), 'content', versionId] as const,
  },

  // Agent Skills (attachments)
  agentSkills: {
    all: ['agent-skills'] as const,
    byAgent: (agentId: string) => [...queryKeys.agentSkills.all, 'agent', agentId] as const,
    resolved: (agentId: string) => [...queryKeys.agentSkills.byAgent(agentId), 'resolved'] as const,
  },

  // Agent Evals
  agentEvals: {
    all: ['aiservices'] as const,
    sessions: {
      all: () => [...queryKeys.agentEvals.all, 'sessions'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.sessions.all(), 'list', { filters }] as const,
      listUnpaginated: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.sessions.all(), 'listUnpaginated', { filters }] as const,
      detail: (id: string) => [...queryKeys.agentEvals.sessions.all(), 'detail', id] as const,
      rows: (id: string, filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.sessions.detail(id), 'rows', { filters }] as const,
      rowsUnpaginated: (id: string, filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.sessions.detail(id), 'rowsUnpaginated', { filters }] as const,
      distribution: (id: string, metricKey?: string) =>
        [...queryKeys.agentEvals.sessions.detail(id), 'metrics', 'distribution', { metricKey }] as const,
    },
    portfolio: (filters?: Record<string, unknown>) =>
      [...queryKeys.agentEvals.all, 'portfolio', { filters }] as const,
    summaryStats: (filters?: Record<string, unknown>) =>
      [...queryKeys.agentEvals.all, 'summaryStats', { filters }] as const,
    trend: (agentId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.agentEvals.all, 'trend', agentId, { filters }] as const,
    catalog: (projectId?: string) =>
      [...queryKeys.agentEvals.all, 'catalog', projectId ?? 'all'] as const,
    glossary: () => [...queryKeys.agentEvals.all, 'glossary'] as const,
    leaderboard: {
      all: () => [...queryKeys.agentEvals.all, 'leaderboard'] as const,
      datasets: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.leaderboard.all(), 'datasets', { filters }] as const,
      board: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.leaderboard.all(), 'board', { filters }] as const,
      stats: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.leaderboard.all(), 'stats', { filters }] as const,
    },
    templates: {
      all: () => [...queryKeys.agentEvals.all, 'templates'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.agentEvals.templates.all(), 'list', { filters }] as const,
      detail: (id: string) => [...queryKeys.agentEvals.templates.all(), 'detail', id] as const,
    },
  },

  // Statistics
  statistics: {
    all: ['statistics'] as const,
    access: () => [...queryKeys.statistics.all, 'access'] as const,
    usage: () => [...queryKeys.statistics.all, 'usage'] as const,
  },

  // AIWorkflows
  workflows: {
    all: ['workflows'] as const,
    lists: () => [...queryKeys.workflows.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.workflows.lists(), { filters }] as const,
    details: () => [...queryKeys.workflows.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workflows.details(), id] as const,
    status: (id: string) => [...queryKeys.workflows.all, 'status', id] as const,
    dependencies: (workflowId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'dependencies'] as const,
    versions: (workflowId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'versions'] as const,
    archivedVersions: (filters?: Record<string, unknown>) =>
      [...queryKeys.workflows.all, 'archived-versions', filters ?? {}] as const,
    versionDetail: (workflowId: string, versionId: string) =>
      [...queryKeys.workflows.versions(workflowId), versionId] as const,
    executions: (workflowId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'executions'] as const,
    allExecutions: (filters?: { project_id?: string; offset?: number; limit?: number }) =>
      [...queryKeys.workflows.all, 'all-executions', filters ?? {}] as const,
    activeExecutions: (filters?: { offset?: number; limit?: number; search?: string }) =>
      [...queryKeys.workflows.all, 'active-executions', filters ?? {}] as const,
    executionDetail: (executionId: string) =>
      [...queryKeys.workflows.all, 'execution', executionId] as const,
    executionNodes: (executionId: string) =>
      [...queryKeys.workflows.all, 'execution', executionId, 'nodes'] as const,
    executionNodeDetails: (executionId: string, nodeId: string) =>
      [...queryKeys.workflows.all, 'execution', executionId, 'nodes', nodeId, 'details'] as const,
    pmaViewCatalog: (executionId: string, nodeId: string) =>
      [...queryKeys.workflows.all, 'execution', executionId, 'nodes', nodeId, 'pma'] as const,
    pmaViewProcess: (executionId: string, nodeId: string, index: number) =>
      [...queryKeys.workflows.all, 'execution', executionId, 'nodes', nodeId, 'pma', index] as const,
    pmaViewBpmn: (executionId: string, nodeId: string, index: number, view: string) =>
      [...queryKeys.workflows.all, 'execution', executionId, 'nodes', nodeId, 'pma', index, 'bpmn', view] as const,
    schedule: (workflowId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'schedule'] as const,
    controls: (workflowId: string, deploymentId?: string) =>
      [
        ...queryKeys.workflows.detail(workflowId),
        'controls',
        deploymentId ?? 'workflow',
      ] as const,
    triggers: (workflowId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'triggers'] as const,
    triggerTokens: (workflowId: string, triggerId: string) =>
      [...queryKeys.workflows.triggers(workflowId), triggerId, 'tokens'] as const,
    nodeRegistry: () => [...queryKeys.workflows.all, 'node-registry'] as const,
    openApiSpec: () => [...queryKeys.workflows.all, 'openapi-spec'] as const,
    executionReference: (workflowId: string, version: 'published' | 'current' = 'published') =>
      [...queryKeys.workflows.detail(workflowId), 'execution-reference', version] as const,
    templates: (filters?: {
      include_archived?: boolean
      search?: string
      created_by?: string | string[]
    }) => [...queryKeys.workflows.all, 'templates', filters ?? {}] as const,
    templateDetail: (id: string) =>
      [...queryKeys.workflows.all, 'templates', id] as const,
  },

  // Deployments
  deployments: {
    all: ['deployments'] as const,
    lists: () => [...queryKeys.deployments.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.deployments.lists(), { filters }] as const,
    details: () => [...queryKeys.deployments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.deployments.details(), id] as const,
    checklist: () => [...queryKeys.deployments.all, 'checklist'] as const,
    evalSummary: (id: string) => [...queryKeys.deployments.detail(id), 'eval-summary'] as const,
    metrics: (id: string, rangeKey: string) =>
      [...queryKeys.deployments.detail(id), 'metrics', rangeKey] as const,
    guardrails: (id: string) =>
      [...queryKeys.deployments.detail(id), 'guardrails'] as const,
    settings: (id: string) =>
      [...queryKeys.deployments.detail(id), 'settings'] as const,
    knowledgeOverrides: (id: string) =>
      [...queryKeys.deployments.detail(id), 'knowledge-overrides'] as const,
  },

  // Agent Store
  agentStore: {
    all: ['agent-store'] as const,
    lists: () => [...queryKeys.agentStore.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.agentStore.lists(), { filters }] as const,
    details: () => [...queryKeys.agentStore.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.agentStore.details(), id] as const,
    ratings: (id: string) => [...queryKeys.agentStore.detail(id), 'ratings'] as const,
    access: (id: string) => [...queryKeys.agentStore.detail(id), 'access'] as const,
    accessRequests: (id: string) =>
      [...queryKeys.agentStore.detail(id), 'access-requests'] as const,
  },

  workflowStore: {
    all: ['workflow-store'] as const,
    lists: () => [...queryKeys.workflowStore.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.workflowStore.lists(), { filters }] as const,
    details: () => [...queryKeys.workflowStore.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workflowStore.details(), id] as const,
    ratings: (id: string) => [...queryKeys.workflowStore.detail(id), 'ratings'] as const,
    access: (id: string) => [...queryKeys.workflowStore.detail(id), 'access'] as const,
    accessRequests: (id: string) =>
      [...queryKeys.workflowStore.detail(id), 'access-requests'] as const,
  },

  // MonitoringService (scheduled online evals / drift monitors)
  monitoringJobs: {
    all: ['monitoring-jobs'] as const,
    catalog: () => [...queryKeys.monitoringJobs.all, 'catalog'] as const,
    estimate: (body: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.all, 'estimate', body] as const,
    monitors: () => [...queryKeys.monitoringJobs.all, 'monitors'] as const,
    monitorList: (params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.monitors(), 'list', params] as const,
    byDeployment: (deploymentId: string) =>
      [...queryKeys.monitoringJobs.monitors(), 'by-deployment', deploymentId] as const,
    monitor: (id: string) => [...queryKeys.monitoringJobs.monitors(), 'detail', id] as const,
    runs: (monitorId: string) => [...queryKeys.monitoringJobs.monitor(monitorId), 'runs'] as const,
    runList: (monitorId: string, params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.runs(monitorId), params] as const,
    run: (runId: string, params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.all, 'run', runId, params] as const,
    events: (monitorId: string, params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.monitor(monitorId), 'events', params] as const,
    customFunctions: (monitorId: string) =>
      [...queryKeys.monitoringJobs.monitor(monitorId), 'custom-functions'] as const,
    timeseries: (monitorId: string, params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.monitor(monitorId), 'timeseries', params] as const,
    performance: (section: string, params: Record<string, unknown>) =>
      [...queryKeys.monitoringJobs.all, 'performance', section, params] as const,
  },

  workflowDeployments: {
    all: ['workflow-deployments'] as const,
    lists: () => [...queryKeys.workflowDeployments.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.workflowDeployments.lists(), { filters }] as const,
    details: () => [...queryKeys.workflowDeployments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workflowDeployments.details(), id] as const,
    schedule: (id: string) => [...queryKeys.workflowDeployments.detail(id), 'schedule'] as const,
    runs: (id: string, environment: string) =>
      [...queryKeys.workflowDeployments.detail(id), 'runs', environment] as const,
    metrics: (id: string, rangeKey: string) =>
      [...queryKeys.workflowDeployments.detail(id), 'metrics', rangeKey] as const,
  },

  // Guardrails Metrics (Agentserve)
  guardrailsMetrics: {
    all: ['guardrails-metrics'] as const,
    dashboard: (days: number, environment?: string) =>
      [...queryKeys.guardrailsMetrics.all, 'dashboard', days, environment ?? 'all'] as const,
    events: (params?: Record<string, unknown>) =>
      [...queryKeys.guardrailsMetrics.all, 'events', { params }] as const,
    agentSummary: (agentId: string, days: number) =>
      [...queryKeys.guardrailsMetrics.all, 'agent-summary', agentId, days] as const,
    agentTrends: (days: number, page: number, pageSize: number, environment?: string) =>
      [...queryKeys.guardrailsMetrics.all, 'agent-trends', days, page, pageSize, environment ?? 'all'] as const,
    engineHealth: (
      days: number,
      page = 1,
      pageSize = 10,
      sortBy = 'engine_name',
      sortOrder: 'asc' | 'desc' = 'asc',
      environment?: string,
    ) =>
      [
        ...queryKeys.guardrailsMetrics.all,
        'engine-health',
        days,
        page,
        pageSize,
        sortBy,
        sortOrder,
        environment ?? 'all',
      ] as const,
  },

  // Memory (Agentserve Memory System)
  memory: {
    all: ['memory'] as const,
    stats: (agentId: string, userId?: string) =>
      [...queryKeys.memory.all, 'stats', agentId, { userId }] as const,
    operations: (
      agentId: string,
      filters?: { userId?: string; operationType?: string; limit?: number }
    ) => [...queryKeys.memory.all, 'operations', agentId, { filters }] as const,
    context: (agentId: string, userId: string, query?: string) =>
      [...queryKeys.memory.all, 'context', agentId, userId, { query }] as const,
    retrievalScores: (agentId: string, userId: string, query: string) =>
      [...queryKeys.memory.all, 'retrieval-scores', agentId, userId, query] as const,
    memories: (agentId: string, userId: string, filters?: Record<string, unknown>) =>
      filters !== undefined
        ? ([...queryKeys.memory.all, 'memories', agentId, userId, { filters }] as const)
        : ([...queryKeys.memory.all, 'memories', agentId, userId] as const),
    health: () => [...queryKeys.memory.all, 'health'] as const,
    status: () => [...queryKeys.memory.all, 'status'] as const,
    /** TAGP tenant default memory embedding configured (vs env fallback) */
    tenantEmbeddingStatus: () => [...queryKeys.memory.all, 'tenant-embedding-status'] as const,
    fileUploadEmbeddingStatus: () =>
      [...queryKeys.memory.all, 'file-upload-embedding-status'] as const,
    // Few-Shot Examples
    fewShot: {
      all: (agentId: string) => [...queryKeys.memory.all, 'few-shot', agentId] as const,
      list: (
        agentId: string,
        filters?: { exampleType?: string; limit?: number; offset?: number }
      ) => [...queryKeys.memory.all, 'few-shot', agentId, 'list', { filters }] as const,
      detail: (agentId: string, exampleId: string) =>
        [...queryKeys.memory.all, 'few-shot', agentId, 'detail', exampleId] as const,
      quality: (agentId: string, exampleId: string) =>
        [...queryKeys.memory.all, 'few-shot', agentId, 'quality', exampleId] as const,
    },
  },

  // Personal Skills (user-scoped)
  personalSkills: {
    all: ['personalSkills'] as const,
    lists: () => [...queryKeys.personalSkills.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.personalSkills.lists(), { filters }] as const,
    details: () => [...queryKeys.personalSkills.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.personalSkills.details(), id] as const,
  },

  // FAQ (Agentserve Agent FAQ)
  faq: {
    all: ['faq'] as const,
    personalized: (agentId: string, environment?: string) =>
      [...queryKeys.faq.all, 'personalized', agentId, { environment }] as const,
  },

  // EO Chat FAQ (UDM / Ontology chat)
  eoChatFaq: {
    all: ['eoChatFaq'] as const,
    personalized: (contextType: string, contextId: string, historySig?: string) =>
      [...queryKeys.eoChatFaq.all, 'personalized', contextType, contextId, historySig ?? ''] as const,
    curated: (contextType: string, contextId: string) =>
      [...queryKeys.eoChatFaq.all, 'curated', contextType, contextId] as const,
  },

  tools: {
    all: ['tools'] as const,
    lists: () => [...queryKeys.tools.all, 'list'] as const,
    listAll: (filters?: Record<string, unknown>) =>
      [...queryKeys.tools.lists(), 'all', filters ?? {}] as const,
  },

  projectKnowledge: {
    all: ['projectKnowledge'] as const,
    listAll: (projectId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.projectKnowledge.all, 'all', projectId, filters ?? {}] as const,
  },

  // AI Blueprints
  blueprints: {
    all: ['ai-blueprints'] as const,
    lists: () => [...queryKeys.blueprints.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters !== undefined
        ? ([...queryKeys.blueprints.lists(), { filters }] as const)
        : ([...queryKeys.blueprints.lists()] as const),
    creators: () => [...queryKeys.blueprints.all, 'all-creators'] as const,
    details: () => [...queryKeys.blueprints.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.blueprints.details(), id] as const,
  },
} as const

/**
 * Type helper for query keys
 * Using any[] as the query keys object is complex and type inference is failing
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QueryKey = readonly any[]
